<!-- src/components/Note/AiNoteGeneratorForm.vue -->
<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useDraggableModal } from "@/composables/useDraggableModal";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

import AddPermissionForm from "@/components/Generics/AddPermissionForm.vue";

import { useTagStore } from "@/stores/useTagStore";
import { useUserStore } from "@/stores/useUserStore";
import { usePermissionStore } from "@/stores/usePermissionStore";
import {
  buildManagerPermissionGrants,
  getDefaultAccessMode,
} from "@/utils/permissionDefaults";
import { useGroupStore } from "@/stores/useGroupStore";

import api from "@/services/api";
import { toPlainText } from "@/utils/htmlProjection";

// ─── Stores ───────────────────────────────────────────────────────────────────
const tagStore = useTagStore();
const userStore = useUserStore();
const permissionStore = usePermissionStore();
const groupStore = useGroupStore();

// ─── Estado de carregamento ───────────────────────────────────────────────────
const isLoading = ref(true);
const isSubmitting = ref(false);
const extraInstructions = ref("");
// >>> NOVO: fonte fixa fornecida pelo usuário para todas as notas do lote.
// Quando vazio, a IA decide a fonte de cada nota (com fallback "Acervo Litterae").
const userSource = ref("");

// ─── Atividade (select paginado com busca) ────────────────────────────────────
const activityOptions = ref([]); // [{ label, value, description }]
const activitySearchTerm = ref("");
const activitySearchPage = ref(1);
const activitySearchTotal = ref(0);
const activityPageSize = 20;
const isLoadingActivities = ref(false);
const selectedActivity = ref(null); // opção selecionada

function autoResize(el) {
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

// A descrição da atividade pode ser HTML (RichTextEditor). Aqui ela só serve
// de rótulo secundário no dropdown — texto puro, nunca marcação — senão a
// opção mostraria tags literais.
function activityDescriptionPreview(a) {
  if (a.contentFormat !== "html") return a.description ?? "";
  try {
    return toPlainText(a.description ?? "").text;
  } catch {
    return "";
  }
}

async function loadActivities(search = "", page = 1) {
  isLoadingActivities.value = true;
  try {
    const res = await api.get("/activities/paged", {
      params: {
        pageNumber: page,
        pageSize: activityPageSize,
        search,
        myActivitiesOnly: false,
      },
    });
    const items = res.data.items ?? [];
    activitySearchTotal.value = res.data.totalCount ?? 0;

    const mapped = items.map((a) => ({
      label: a.title,
      value: a.activityId,
      description: activityDescriptionPreview(a),
      textTitle: a.textTitle ?? "",
    }));

    if (page === 1) {
      activityOptions.value = mapped;
    } else {
      activityOptions.value = [...activityOptions.value, ...mapped];
    }
    activitySearchPage.value = page;
  } catch (e) {
    console.error("Erro ao carregar atividades:", e);
  } finally {
    isLoadingActivities.value = false;
  }
}

function onActivitySearch(query) {
  activitySearchTerm.value = query;
  loadActivities(query, 1);
}

function onActivityLoadMore() {
  const hasMore = activityOptions.value.length < activitySearchTotal.value;
  if (!hasMore || isLoadingActivities.value) return;
  loadActivities(activitySearchTerm.value, activitySearchPage.value + 1);
}

// ─── Tags ─────────────────────────────────────────────────────────────────────
const allTags = ref([]);
const selectedTag = ref([]);

function buildTagOptions() {
  allTags.value = tagStore.tags.map((t) => ({
    label: t.tagName ?? "",
    value: t.tagId,
  }));
}

// ─── Quantidade ───────────────────────────────────────────────────────────────
const noteCount = ref(1);
const approximateBodyLength = ref(120);
// >>> NOVO: modo de ancoragem das notas geradas pela IA.
// "normal"      → descarta notas sem trecho localizado (padrão).
// "allow_loose" → persiste notas sem trecho como avulsas.
// "only_loose"  → força todos os offsets a 0/0 — todas as notas são avulsas.
const noteAnchorMode = ref("normal");

const anchorModeOptions = [
  {
    value: "normal",
    label: "Normal",
    hint: "Descarta notas sem trecho localizado",
  },
  {
    value: "allow_loose",
    label: "Permitir avulsas",
    hint: "Notas sem trecho são salvas com offsets 0",
  },
  {
    value: "only_loose",
    label: "Apenas avulsas",
    hint: "Todos os offsets são forçados a 0",
  },
];

// ─── Permissões (idêntico ao AddNoteForm) ────────────────────────────────────
const permissionFormRef = ref(null);
const permissionFormKey = ref(0);
const showPermissionModal = ref(false);
const permissionBackup = ref([]);
const permissionMode = ref("public");

// 🔹 NOVO: arrasto do mini-modal de permissões pelo seu cabeçalho.
// O v-if recria o DOM a cada abertura, mas o offset vive aqui — daí o reset.
const {
  panelRef: permPanelRef,
  panelStyle: permPanelStyle,
  isDragging: isPermDragging,
  isDraggable: isPermDraggable,
  onHandlePointerDown: onPermHandlePointerDown,
  resetPosition: resetPermPosition,
} = useDraggableModal();

watch(showPermissionModal, (val) => {
  if (val) resetPermPosition();
});

const permissions = ref([]);

const loggedUser = computed(() => userStore.currentUser);
const creatorUserId = computed(() => loggedUser.value?.userId ?? null);
const creatorUserName = computed(() => loggedUser.value?.userName ?? "");

function getTodosGroupId() {
  // Preferência: o "Todos" DO CÍRCULO — grupo mantido pelo sistema com o titular
  // e seus membros ativos. Sem círculo (usuário individual), cai no grupo global
  // da plataforma, como antes.
  const circleDefault = groupStore.groups.find(
    (g) => g.isCircleDefault === true
  );
  if (circleDefault) return circleDefault.groupId ?? null;

  return (
    groupStore.groups.find((g) => g.groupName?.toLowerCase() === "todos")
      ?.groupId ?? null
  );
}
function getPermissionIdByName(name) {
  return (
    permissionStore.permissions.find(
      (p) => p.permissionName?.toLowerCase() === name.toLowerCase()
    )?.permissionId ?? null
  );
}

function buildPublicPermissions() {
  const todosId = getTodosGroupId();
  const creatorId = creatorUserId.value;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");
  const perms = [];
  if (todosId && visualizarId && selecionarId) {
    perms.push(
      { entityId: todosId, type: "group", permissionId: visualizarId },
      { entityId: todosId, type: "group", permissionId: selecionarId }
    );
  }
  if (creatorId && visualizarId && selecionarId && editarId) {
    perms.push(
      { entityId: creatorId, type: "user", permissionId: visualizarId },
      { entityId: creatorId, type: "user", permissionId: selecionarId },
      { entityId: creatorId, type: "user", permissionId: editarId }
    );
  }
  // Contexto de membro gerenciado: o titular acompanha o criador em tudo
  // que o membro publica (ver utils/permissionDefaults.js).
  perms.push(
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId })
  );

  return perms;
}

// Molde fechado: só o criador e, em contexto de membro gerenciado, o titular.
// Estes formulários não têm o botão "Privado" — este molde existe para o membro
// gerenciado, cuja nota nasce visível apenas para ele e para o titular.
function buildPrivatePermissions() {
  const creatorId = creatorUserId.value;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");
  if (!creatorId || !visualizarId || !selecionarId || !editarId) return [];
  return [
    { entityId: creatorId, type: "user", permissionId: visualizarId },
    { entityId: creatorId, type: "user", permissionId: selecionarId },
    { entityId: creatorId, type: "user", permissionId: editarId },
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId }),
  ];
}

// Molde com que o formulário abre. Membro gerenciado começa fechado; para os
// demais o padrão histórico continua sendo público.
function buildInitialPermissions() {
  return getDefaultAccessMode() === "private"
    ? buildPrivatePermissions()
    : buildPublicPermissions();
}

function isNoteFullyPublic(perms) {
  const todosId = getTodosGroupId();
  if (!todosId) return false;
  return perms.some((p) => p.type === "group" && p.entityId === todosId);
}

const includesTodosGroup = computed(() => isNoteFullyPublic(permissions.value));

function openPermissionModal() {
  permissionBackup.value = (permissions.value || []).map((p) => ({ ...p }));
  permissionMode.value = "custom";
  if (!permissions.value.length) {
    permissions.value = buildPublicPermissions();
  }
  showPermissionModal.value = true;
}
function cancelPermissionModal() {
  permissions.value = (permissionBackup.value || []).map((p) => ({ ...p }));
  showPermissionModal.value = false;
}
function confirmPermissionModal() {
  if (
    permissionFormRef.value &&
    typeof permissionFormRef.value.validateBeforeConfirm === "function" &&
    !permissionFormRef.value.validateBeforeConfirm()
  )
    return;
  showPermissionModal.value = false;
}

const userListForChild = computed(() =>
  (userStore.users || []).map((u) => ({ id: u.userId, name: u.userName }))
);
const groupListForChild = computed(() =>
  (groupStore.groups || []).map((g) => ({
    id: g.groupId,
    name: g.groupName,
    createdByUserId: g.createdByUserId ?? g.CreatedByUserId ?? null,
    createdByUserName: g.createdByUserName ?? g.CreatedByUserName ?? null,
    // Membros do grupo: o AddPermissionForm usa para avisar quando a linha de
    // um usuário substitui a permissão que ele teria pelo grupo (precedência).
    members: g.users ?? g.Users ?? [],
  }))
);
const permissionListForChild = computed(() =>
  (permissionStore.permissions || []).map((p) => ({
    id: p.permissionId,
    name: p.permissionName,
  }))
);

// ─── Toast ────────────────────────────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastType = ref("error");
const toastTimeoutId = ref(null);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, 4000);
}
function showErrorToast(msg) {
  toastType.value = "error";
  toastMessage.value = msg;
  showToast.value = true;
  resetToastTimeout();
}
function showSuccessToast(msg) {
  toastType.value = "success";
  toastMessage.value = msg;
  showToast.value = true;
  resetToastTimeout();
}

// ─── Resultado ────────────────────────────────────────────────────────────────
// Notas retornadas pela API após geração bem-sucedida
const generatedNotes = ref([]);
const warnings = ref([]);
const showResults = ref(false);

// ─── Submissão ────────────────────────────────────────────────────────────────
async function gerar() {
  if (isSubmitting.value) return;

  // Validações
  if (!selectedActivity.value) {
    showErrorToast("Selecione uma atividade.");
    return;
  }
  // >>> NOVO: valida comprimento da fonte antes de enviar
  if (userSource.value.trim().length > 300) {
    showErrorToast("A fonte não pode exceder 300 caracteres.");
    return;
  }
  // Tags são opcionais — sem seleção o backend usa todas as tags fornecidas
  if (noteCount.value < 1 || noteCount.value > 20) {
    showErrorToast("A quantidade deve ser entre 1 e 20.");
    return;
  }
  // >>> validação de tamanho do corpo
  if (approximateBodyLength.value > 3000) {
    showErrorToast("O tamanho aproximado do corpo não pode exceder 3000 caracteres.");
    return;
  }

  // Garante permissões públicas se modo for público
  if (permissionMode.value === "public" && !permissions.value.length) {
    permissions.value = buildPublicPermissions();
  }

  isSubmitting.value = true;
  generatedNotes.value = [];
  warnings.value = [];
  showResults.value = false;

  try {
    const payload = {
      activityId: selectedActivity.value.value,
      tagIds: selectedTag.value.map((t) => t.value),
      count: noteCount.value,
      approximateBodyLength: approximateBodyLength.value,
      extraInstructions: extraInstructions.value.trim() || null,
      // >>> NOVO: fonte fixa — null se vazio (IA decide por nota)
      userSource: userSource.value.trim() || null,
      // >>> NOVO: repassa o modo de ancoragem ao backend
      noteAnchorMode: noteAnchorMode.value,
      permissions: permissions.value,
    };

    const res = await api.post("/notes/generate-with-ai", payload);
    const data = res.data;

    generatedNotes.value = data.createdNotes ?? [];
    warnings.value = data.warnings ?? [];
    showResults.value = true;

    if (generatedNotes.value.length > 0) {
      showSuccessToast(
        `${generatedNotes.value.length} nota(s) gerada(s) com sucesso!`
      );
    } else {
      showErrorToast("Nenhuma nota foi criada. Verifique os avisos abaixo.");
    }
  } catch (error) {
    const msg =
      error.response?.data?.message ||
      error.message ||
      "Erro desconhecido ao gerar notas.";
    showErrorToast(msg);
  } finally {
    isSubmitting.value = false;
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      tagStore.fetchTags(),
      permissionStore.fetchAllPermissions(),
      groupStore.fetchGroups(1, 1000),
      userStore.fetchUsers(1, 1000),
      loadActivities("", 1),
    ]);
    buildTagOptions();
    permissions.value = buildInitialPermissions();
  } finally {
    isLoading.value = false;
  }
});

onBeforeUnmount(() => {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div class="ai-page">
    <!-- ── Cabeçalho da página ─────────────────────────────────────────── -->
    <div class="ai-page-header">
      <div class="ai-page-header-left">
        <span class="ai-icon-wrap">
          <i class="fas fa-robot"></i>
        </span>
        <div>
          <h2 class="ai-page-title">Gerador de Notas com IA</h2>
          <p class="ai-page-subtitle">
            Selecione uma atividade e tags — a IA lerá o texto e criará notas
            automaticamente.
          </p>
        </div>
      </div>
    </div>

    <!-- ── Loading overlay ────────────────────────────────────────────── -->
    <transition name="loading-fade">
      <div v-if="isLoading" class="loading-overlay loading-overlay--translucent" aria-label="Carregando...">
        <svg class="loading-spinner" viewBox="0 0 24 24" fill="none">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
        <span class="loading-text">Carregando...</span>
      </div>
    </transition>

    <div v-if="!isLoading" class="ai-body">
      <!-- ── Formulário ──────────────────────────────────────────────── -->
      <div class="ai-form-panel">
        <!-- Atividade -->
        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-tasks label-icon"></i>
            Atividade
          </label>
          <Multiselect
            v-model="selectedActivity"
            :options="activityOptions"
            :loading="isLoadingActivities"
            :internal-search="false"
            :searchable="true"
            placeholder="Busque e selecione uma atividade..."
            label="label"
            track-by="value"
            :allow-empty="false"
            select-label="Tecle enter para selecionar"
            deselect-label="Tecle enter para remover"
            selected-label="Selecionado"
            @search-change="onActivitySearch"
            @open="() => loadActivities('', 1)"
          >
            <template #singleLabel="{ option }">
              <span class="ms-selected-activity">
                <span class="ms-selected-title">{{ option.label }}</span>
                <span v-if="option.textTitle" class="ms-selected-text-sub">{{
                  option.textTitle
                }}</span>
              </span>
            </template>
            <template #afterList>
              <div
                v-if="activityOptions.length < activitySearchTotal"
                class="ms-load-more"
                @mousedown.prevent="onActivityLoadMore"
              >
                <i class="fas fa-chevron-down"></i> Carregar mais
              </div>
            </template>
            <template #noResult>
              <span class="ms-no-result">Nenhuma atividade encontrada.</span>
            </template>
            <template #option="{ option }">
              <div class="ms-activity-option">
                <span class="ms-activity-label">{{ option.label }}</span>
                <span v-if="option.textTitle" class="ms-activity-text-sub">{{
                  option.textTitle
                }}</span>
                <span v-if="option.description" class="ms-activity-desc">{{
                  option.description
                }}</span>
              </div>
            </template>
          </Multiselect>
        </div>

        <!-- Tags -->
        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-hashtag label-icon"></i>
            Tags
          </label>
          <Multiselect
            v-model="selectedTag"
            :options="allTags"
            :multiple="true"
            placeholder="Selecione tags para guiar a geração..."
            label="label"
            track-by="value"
            :allow-empty="true"
            select-label="Tecle enter para selecionar"
            deselect-label="Tecle enter para remover"
            selected-label="Selecionado"
            :disabled="isSubmitting"
          >
            <template #tag="{ option, remove }">
              <span class="ms-chip">
                {{ option.label }}
                <button
                  type="button"
                  class="ms-chip-remove"
                  @click.stop="remove(option)"
                  aria-label="Remover"
                >
                  ×
                </button>
              </span>
            </template>
          </Multiselect>
        </div>

        <!-- Instruções adicionais ao prompt -->
        <div class="form-group">
          <label class="form-label" for="extra-instructions">
            <i class="fas fa-pen-to-square label-icon"></i>
            Prompt adicional
            <span class="form-label-hint">(opcional)</span>
          </label>
          <textarea
            id="extra-instructions"
            v-model="extraInstructions"
            class="extra-instructions-textarea"
            placeholder="Ex.: Foque em figuras de linguagem. Evite análises históricas."
            rows="2"
            :disabled="isSubmitting"
            @input="autoResize($event.target)"
          ></textarea>
          <span v-if="extraInstructions.length > 0" class="source-char-count">
            {{ extraInstructions.length }}
          </span>
        </div>

        <!-- >>> NOVO: Fonte fixa para todas as notas geradas -->
        <div class="form-group">
          <label class="form-label" for="user-source">
            <i class="fas fa-link label-icon"></i>
            Fonte
            <span class="form-label-hint">(opcional · máx. 300 chars)</span>
          </label>
          <textarea
            id="user-source"
            v-model="userSource"
            maxlength="300"
            class="source-input"
            :class="{ 'source-input--error': userSource.length > 300 }"
            placeholder="Ex.: Dom Casmurro, cap. 2 — deixe vazio para a IA decidir"
            rows="2"
            :disabled="isSubmitting"
            @input="autoResize($event.target)"
          ></textarea>
          <span v-if="userSource.length > 0" class="source-char-count" :class="{ 'source-char-count--warn': userSource.length > 270 }">
            {{ userSource.length }}/300
          </span>
        </div>

        <!-- Quantidade -->
        <div class="form-group form-group--inline">
          <label class="form-label" for="note-count">
            <i class="fas fa-list-ol label-icon"></i>
            Quantidade de notas
          </label>
          <div class="count-control">
            <button
              type="button"
              class="count-btn"
              :disabled="noteCount <= 1 || isSubmitting"
              @click="noteCount = Math.max(1, noteCount - 1)"
            >
              −
            </button>
            <input
              id="note-count"
              v-model.number="noteCount"
              type="number"
              min="1"
              max="20"
              class="count-input"
              :disabled="isSubmitting"
            />
            <button
              type="button"
              class="count-btn"
              :disabled="noteCount >= 20 || isSubmitting"
              @click="noteCount = Math.min(20, noteCount + 1)"
            >
              +
            </button>
          </div>
        </div>

        <!-- Tamanho aproximado do corpo -->
        <div class="form-group form-group--inline">
          <label class="form-label" for="body-length">
            <i class="fas fa-align-left label-icon"></i>
            Tamanho aprox. do corpo
            <span class="form-label-hint">(chars, 0 = livre)</span>
          </label>
          <div class="count-control">
            <button
              type="button"
              class="count-btn"
              :disabled="approximateBodyLength <= 0 || isSubmitting"
              @click="
                approximateBodyLength = Math.max(0, approximateBodyLength - 100)
              "
            >
              −
            </button>
            <input
              id="body-length"
              v-model.number="approximateBodyLength"
              type="number"
              min="0"
              max="3000"
              step="100"
              class="count-input count-input--wide"
              :disabled="isSubmitting"
              @input="approximateBodyLength = Math.min(3000, Math.max(0, Number($event.target.value) || 0))"
            />
            <button
              type="button"
              class="count-btn"
              :disabled="approximateBodyLength >= 3000 || isSubmitting"
              @click="
                approximateBodyLength = Math.min(
                  3000,
                  approximateBodyLength + 100
                )
              "
            >
              +
            </button>
          </div>
        </div>

        <!-- Modo de ancoragem — dropdown -->
        <div class="form-group">
          <label class="form-label" for="anchor-mode-select">
            <i class="fas fa-anchor label-icon"></i>
            Modo de ancoragem
          </label>
          <div class="anchor-select-wrapper">
            <select
              id="anchor-mode-select"
              v-model="noteAnchorMode"
              class="anchor-select"
              :disabled="isSubmitting"
            >
              <option
                v-for="opt in anchorModeOptions"
                :key="opt.value"
                :value="opt.value"
              >{{ opt.label }}</option>
            </select>
            <!-- Descrição da opção selecionada exibida abaixo do select -->
            <p class="anchor-select-hint">
              {{ anchorModeOptions.find(o => o.value === noteAnchorMode)?.hint }}
            </p>
          </div>
        </div>

        <!-- Permissões -->
        <div class="form-group access-level-group">
          <label class="form-label">
            <i class="fas fa-lock label-icon"></i>
            Permissões
          </label>
          <div class="access-buttons-container">
            <div
              class="public-access-indicator"
              :class="{
 'public-access-indicator--active': includesTodosGroup,
                'public-access-indicator--inactive': !includesTodosGroup,
              }"
            >
              <i class="fas fa-globe-americas btn-icon-fa"></i>
              <span class="btn-text">{{
                includesTodosGroup ? "Público" : "Restrito"
              }}</span>
            </div>
            <button
              type="button"
              class="btn-access-toggle"
              :disabled="isSubmitting"
              @click="openPermissionModal"
            >
              <i class="fas fa-user-shield btn-icon-fa"></i>
              <span class="btn-text">Permissões</span>
            </button>
          </div>
        </div>

        <!-- Botão gerar -->
        <div class="form-footer">
          <button
            type="button"
            class="btn btn-generate"
            :disabled="isSubmitting || isLoading"
            @click="gerar"
          >
            <svg
              v-if="isSubmitting"
              class="btn-spinner"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <i v-else class="fas fa-wand-magic-sparkles"></i>
            <span>{{
              isSubmitting ? "Gerando notas..." : "Gerar notas com IA"
            }}</span>
          </button>
        </div>
      </div>

      <!-- ── Painel de resultados ────────────────────────────────────── -->
      <div class="ai-results-panel">
        <!-- Placeholder quando não há resultados ainda -->
        <div
          v-if="!showResults && !isSubmitting"
          class="ai-results-placeholder"
        >
          <i class="fas fa-robot placeholder-icon"></i>
          <p class="placeholder-text">
            As notas geradas pela IA aparecerão aqui após a geração.
          </p>
        </div>

        <!-- Spinner enquanto gera -->
        <div v-if="isSubmitting" class="ai-generating">
          <svg class="generating-spinner" viewBox="0 0 24 24" fill="none">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <p class="generating-text">
            A IA está lendo o texto e gerando notas…
          </p>
        </div>

        <!-- Resultados -->
        <template v-if="showResults && !isSubmitting">
          <!-- Avisos -->
          <div v-if="warnings.length" class="warnings-block">
            <div class="warnings-header">
              <i class="fas fa-triangle-exclamation"></i>
              <span>{{ warnings.length }} aviso(s)</span>
            </div>
            <ul class="warnings-list">
              <li v-for="(w, i) in warnings" :key="i" class="warning-item">
                {{ w }}
              </li>
            </ul>
          </div>

          <!-- Contador -->
          <div class="results-summary">
            <i class="fas fa-check-circle results-summary-icon"></i>
            <span>
              <strong>{{ generatedNotes.length }}</strong>
              nota(s) criada(s) com sucesso
            </span>
          </div>

          <!-- Cards das notas -->
          <div class="notes-list">
            <div
              v-for="note in generatedNotes"
              :key="note.noteId"
              class="note-card"
            >
              <!-- Tags da nota -->
              <div
                v-if="note.tagNames && note.tagNames.length"
                class="note-card-tags"
              >
                <span
                  v-for="(tag, ti) in note.tagNames"
                  :key="ti"
                  class="note-tag-chip"
                  >{{ tag }}</span
                >
              </div>

              <!-- Título -->
              <h4 class="note-card-title">{{ note.title }}</h4>

              <!-- Corpo -->
              <p v-if="note.body" class="note-card-body">{{ note.body }}</p>

              <!-- Fonte -->
              <div v-if="note.source" class="note-card-source-plain">
                <i class="fas fa-link source-plain-icon"></i>
                <span class="source-plain-text">{{ note.source }}</span>
              </div>

              <!-- Excerto escolhido pela IA -->
              <div v-if="note.excerpt" class="note-card-excerpt">
                <i class="fas fa-quote-left excerpt-icon"></i>
                <span class="excerpt-text">{{ note.excerpt }}</span>
              </div>

              <!-- Offsets — exibidos sempre -->
              <div class="note-card-range">
                <i class="fas fa-location-crosshairs range-icon"></i>
                <span>
                  Trecho:
                  <strong>{{ note.textRangeStart ?? 0 }}</strong>
                  –
                  <strong>{{ note.textRangeEnd ?? 0 }}</strong>
                  <span
                    v-if="!note.textRangeStart && !note.textRangeEnd"
                    class="range-not-found"
                    >(não localizado)</span
                  >
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ── Modal de permissões ────────────────────────────────────────── -->
    <Teleport to="body">
      <transition name="permission-modal-fade">
        <div v-if="showPermissionModal" class="permission-modal-backdrop">
          <div
            ref="permPanelRef"
            class="permission-modal-panel"
            :style="permPanelStyle"
            @click.stop
          >
            <div
              class="permission-modal-header"
              :class="{
 'permission-modal-header--draggable': isPermDraggable,
                'permission-modal-header--dragging': isPermDragging,
              }"
              @pointerdown="onPermHandlePointerDown"
            >
              <h4 class="permission-modal-title">Editar Permissões</h4>
              <button
                type="button"
                class="permission-modal-close"
                @click="cancelPermissionModal"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <div class="permission-modal-body">
              <p class="permission-modal-instruction">
                Defina aqui usuários, grupos e permissões para as notas geradas.
              </p>
              <AddPermissionForm
                ref="permissionFormRef"
                v-model:payload="permissions"
                :key="permissionFormKey"
                :users="userListForChild"
                :groups="groupListForChild"
                :permissions="permissionListForChild"
                entityType="Note"
                :creatorUserName="creatorUserName"
              />
            </div>
            <div class="permission-modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                @click="cancelPermissionModal"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="btn btn-success"
                @click="confirmPermissionModal"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- ── Toast ─────────────────────────────────────────────────────── -->
    <Teleport to="body">
      <transition name="toast-fade">
        <div
          v-if="showToast"
          class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
        >
          <div
            :class="[
 'toast pointer-events-auto',
              toastType === 'error' ? 'toast-error' : 'toast-success',
            ]"
          >
            <div class="toast-icon">
              <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
                <path
                  v-if="toastType === 'error'"
                  d="M10 5v6M10 13.5v1.5"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                />
                <path
                  v-else
                  d="M7.5 10.5 9.5 12.5 13 8.5"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="toast-content">
              <p class="toast-title">
                {{ toastType === "error" ? "Erro" : "Sucesso!" }}
              </p>
              <p class="toast-message">{{ toastMessage }}</p>
            </div>
            <button
              type="button"
              class="toast-close"
              @click="showToast = false"
            >
              ×
            </button>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<style scoped>
.loading-overlay {
  border-radius: var(--radius-2xl);
}

/* ── Página ─────────────────────────────────────────────────────────── */
.ai-page {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  min-height: 0;
  height: 100%;
  box-sizing: border-box;
}

/* ── Cabeçalho ──────────────────────────────────────────────────────── */
.ai-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.ai-page-header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.ai-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background-color: var(--brand-light);
  color: #1a1a2e;
  font-size: 1.25rem;
  flex-shrink: 0;
}
.ai-page-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
}
.ai-page-subtitle {
  font-size: 0.8rem;
  color: var(--text-light);
  margin: 2px 0 0;
}

/* ── Loading ────────────────────────────────────────────────────────── */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ── Body (2 colunas) ───────────────────────────────────────────────── */
.ai-body {
  display: flex;
  gap: 2rem;
  flex: 1 1 auto;
  min-height: 0;
}

/* ── Painel formulário ──────────────────────────────────────────────── */
.ai-form-panel {
  flex: 0 0 420px;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  border-right: 1px solid var(--border-color);
  padding-right: 2rem;
  overflow-y: auto;
}

/* ── Painel resultados ──────────────────────────────────────────────── */
.ai-results-panel {
  flex: 1 1 0;
  min-width: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group--inline {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.form-label-hint {
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-light);
}
.label-icon {
  color: var(--brand);
  font-size: 0.85rem;
}

/* Contador */
.count-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.count-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--bg-header);
  color: var(--text-dark);
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.count-btn:hover:not(:disabled) {
  background: var(--neutral-200);
}
.count-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.count-input {
  width: 52px;
  text-align: center;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 4px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-dark);
}
.count-input::-webkit-inner-spin-button,
.count-input::-webkit-outer-spin-button {
  opacity: 0;
}

/* Acesso */
.access-level-group {
  flex-direction: row;
  align-items: center;
  gap: 1rem;
}
.access-buttons-container {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}
.public-access-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.85rem;
  border-radius: 10px;
  border: 1px dashed var(--neutral-400);
  background: var(--surface-2);
  color: var(--text-muted);
  font-size: 0.78rem;
  font-weight: 500;
}
.public-access-indicator--active {
  border-color: var(--success);
  background: var(--success-bg);
  color: var(--success-text);
}
.public-access-indicator--inactive {
  border-color: var(--danger);
  background: var(--danger-bg);
  color: var(--danger-text);
}
.btn-access-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 0.65rem;
  border-radius: 9px;
  background: var(--neutral-200);
  border: 1px solid var(--border-color);
  color: var(--text-dark);
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
  transition: all 0.2s;
}
.btn-access-toggle:hover:not(:disabled) {
  background: var(--neutral-300);
}
.btn-access-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-icon-fa {
  font-size: 0.85rem;
}

/* Footer do form */
.form-footer {
  margin-top: auto;
  padding-top: 0.5rem;
}

.btn-generate {
  width: 100%;
  height: 42px;
  font-size: 0.95rem;
  background: linear-gradient(135deg, var(--brand-light), var(--brand));
  color: #1a1a2e;
  box-shadow: 0 4px 14px rgba(155, 132, 255, 0.35);
}
.btn-generate:hover:not(:disabled) {
  background: linear-gradient(135deg, #a99cf5, var(--brand-hover));
  box-shadow: 0 6px 18px rgba(155, 132, 255, 0.45);
  transform: translateY(-1px);
}

.btn-spinner {
  width: 18px;
  height: 18px;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

/* ── Multiselect helpers ────────────────────────────────────────────── */
.ms-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 7px;
  background: var(--surface-3);
  font-size: 0.82rem;
  margin: 2px 4px 2px 0;
}
.ms-chip-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  color: var(--text-muted);
  padding: 0 2px;
  line-height: 1;
}
.ms-chip-remove:hover {
  color: #0f172a;
}
.ms-load-more {
  padding: 8px 12px;
  text-align: center;
  font-size: 0.82rem;
  color: var(--brand);
  cursor: pointer;
  border-top: 1px solid var(--surface-3);
}
.ms-load-more:hover {
  background: #f8f5ff;
}
.ms-no-result {
  font-size: 0.82rem;
  color: var(--text-subtle);
  padding: 8px 12px;
}
.ms-activity-option {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.ms-activity-label {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-dark);
  line-height: 1.3;
}
:deep(.multiselect__option--highlight) .ms-activity-label,
:deep(.multiselect__option--highlight) .ms-activity-desc,
:deep(.multiselect__option--highlight) .ms-activity-text-sub,
:deep(.multiselect__option--highlight) .ms-selected-title,
:deep(.multiselect__option--highlight) .ms-selected-text-sub {
  color: #fff !important;
}
.ms-activity-text-sub {
  font-size: 0.73rem;
  color: var(--text-subtle);
  line-height: 1.2;
}
.ms-activity-desc {
  font-size: 0.72rem;
  color: var(--text-light);
  margin-top: 1px;
}
.ms-selected-activity {
  display: flex;
  flex-direction: column;
  gap: 1px;
  line-height: 1;
}
.ms-selected-title {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-dark);
  line-height: 1.3;
}
.ms-selected-text-sub {
  font-size: 0.73rem;
  color: var(--text-subtle);
  line-height: 1.2;
}
.count-input--wide {
  width: 70px;
}

/* ── Placeholder resultados ──────────────────────────────────────────── */
.ai-results-placeholder {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: var(--text-light);
  text-align: center;
  padding: 2rem;
}
.placeholder-icon {
  font-size: 3rem;
  opacity: 0.25;
}
.placeholder-text {
  font-size: 0.9rem;
  max-width: 280px;
  line-height: 1.5;
}

/* ── Gerando spinner ─────────────────────────────────────────────────── */
.ai-generating {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: var(--brand);
}
.generating-spinner {
  width: 3rem;
  height: 3rem;
  animation: spin 0.9s linear infinite;
}
.generating-text {
  font-size: 0.9rem;
  color: var(--text-light);
}

/* ── Avisos ──────────────────────────────────────────────────────────── */
.warnings-block {
  background: var(--warning-bg);
  border: 1px solid var(--warning-bg);
  border-radius: 12px;
  padding: 0.75rem 1rem;
}
.warnings-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--warning-text);
  margin-bottom: 0.5rem;
}
.warnings-header i {
  color: var(--warning);
}
.warnings-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.warning-item {
  font-size: 0.8rem;
  color: #78350f;
  padding-left: 1rem;
  position: relative;
}
.warning-item::before {
  content: "•";
  position: absolute;
  left: 0;
  color: var(--warning);
}

/* ── Resumo ──────────────────────────────────────────────────────────── */
.results-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--success-text);
}
.results-summary-icon {
  color: var(--success);
  font-size: 1.1rem;
}

/* ── Cards das notas ─────────────────────────────────────────────────── */
.notes-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.note-card {
  background: var(--bg-content);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.2s;
}
.note-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.note-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.note-tag-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, #f0ebff, #e8e3ff);
  color: #4c1d95;
  font-size: 0.72rem;
  font-weight: 600;
}

.note-card-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
  line-height: 1.3;
}
.note-card-body {
  font-size: 0.85rem;
  color: var(--text-light);
  line-height: 1.55;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.note-card-source-plain {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: var(--text-muted);
}
.source-plain-icon {
  color: var(--text-subtle);
  font-size: 0.72rem;
  flex-shrink: 0;
}
.source-plain-text {
  line-height: 1.4;
}

.note-card-range {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  color: var(--text-subtle);
  font-family: monospace;
}
.range-icon {
  color: #c4b5fd;
  font-size: 0.7rem;
}
.range-not-found {
  font-size: 0.72rem;
  color: var(--text-subtle);
  margin-left: 4px;
  font-style: italic;
}

.note-card-excerpt {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  background: #f8f5ff;
  border-left: 3px solid var(--brand);
  border-radius: 0 8px 8px 0;
  padding: 0.5rem 0.75rem;
  margin-top: 2px;
}
.excerpt-icon {
  color: var(--brand);
  font-size: 0.7rem;
  margin-top: 3px;
  flex-shrink: 0;
}
.excerpt-text {
  font-size: 0.78rem;
  color: #4c1d95;
  font-style: italic;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Modal permissões ────────────────────────────────────────────────── */
.permission-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10100;
  background: rgba(15, 23, 42, 0.2);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 50px;
  padding-left: 1rem;
  padding-right: 1rem;
}
.permission-modal-panel {
  width: 590px;
  max-width: 100%;
  height: 760px;
  max-height: calc(100vh - 100px);
  background: var(--bg-content);
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-modal-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.permission-modal-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-header);
}
/* 🔹 NOVO: cabeçalho do mini-modal como alça de arrasto.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.permission-modal-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.permission-modal-header--dragging {
  cursor: grabbing;
}
/* Os botões do cabeçalho continuam sendo clique, não alça */
.permission-modal-header--draggable button {
  cursor: pointer;
}

.permission-modal-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}
.permission-modal-close {
  color: var(--text-light);
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 0.35rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.permission-modal-close:hover {
  background: rgba(0, 0, 0, 0.08);
  color: var(--text-dark);
}
.permission-modal-body {
  padding: 0.8rem 1rem 0.5rem;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.permission-modal-instruction {
  font-size: 0.82rem;
  color: var(--text-light);
  margin-bottom: 0.75rem;
}
.permission-modal-footer {
  flex: 0 0 auto;
  padding: 0.6rem 1rem 0.75rem;
  border-top: 1px solid var(--border-color);
  background: var(--bg-header);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
.permission-modal-fade-enter-active,
.permission-modal-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.18s ease;
}
.permission-modal-fade-enter-from,
.permission-modal-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.toast-title {
  margin-bottom: 0.2rem;
}

:deep(.permission-form-wrapper) {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
:deep(.mt-5) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 1rem !important;
}
:deep(.table-wrap) {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  border-radius: 8px;
  background: var(--bg-content);
  margin-top: 0.5rem;
}
.extra-instructions-textarea {
  width: 100%;
  min-height: 60px;
  max-height: 220px;
  resize: none;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  color: var(--text-dark);
  background: var(--bg-content);
  line-height: 1.5;
  font-family: inherit;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.extra-instructions-textarea:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: var(--focus-ring-brand);
}
.extra-instructions-textarea:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.extra-instructions-textarea::placeholder {
  color: var(--text-light);
  font-style: italic;
}
/* ── Dropdown de modo de ancoragem ─────────────────────────────────── */
.anchor-select-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.anchor-select {
  width: 100%;
  padding: 0.45rem 2rem 0.45rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--bg-content);
  color: var(--text-dark);
  font-size: 0.88rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%239b84ff' stroke-width='1.8' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.anchor-select:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: var(--focus-ring-brand);
}
.anchor-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.anchor-select-hint {
  font-size: 0.75rem;
  color: var(--text-light);
  line-height: 1.4;
  margin: 0;
  padding-left: 2px;
}

/* ── Campo fonte ─────────────────────────────────────────────────────── */
.source-input {
  width: 100%;
  min-height: 60px;
  max-height: 160px;
  resize: none;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  color: var(--text-dark);
  background: var(--bg-content);
  line-height: 1.5;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
}
.source-input:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: var(--focus-ring-brand);
}
.source-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.source-input::placeholder {
  color: var(--text-light);
  font-style: italic;
}
.source-input--error {
  border-color: var(--danger);
  box-shadow: var(--focus-ring-danger);
}
.source-char-count {
  font-size: 0.72rem;
  color: var(--text-light);
  text-align: right;
  display: block;
  margin-top: 2px;
}
.source-char-count--warn {
  color: var(--warning);
  font-weight: 600;
}
</style>
